import { useEffect, useMemo, useState } from "react";
import { Button, Skeleton, Typography } from "@/components/atoms";
import { DataTable, DataTableSorting, Pagination } from "@/components/molecules";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useListFavouritePassengersQuery } from "@/redux/apis/favouritePassenger/favouritePassengerApi";
import { AddEditGuestModal } from "./components/AddEditGuestModal";
import { GuestPassengersEmptyState } from "./components/GuestPassengersEmptyState";
import { guestPassengersColumns } from "./fixtures";
import { generateGuestPassengersData, sortGuestPassengers } from "./helpers";
import { GuestPassengersSortKey } from "./types";

export const GuestPassengersTable = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sorting, setSorting] = useState<DataTableSorting>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const { accountId } = useClientScope();

  // Mobile parity: fetch every guest at once, then sort/paginate client-side (matches app-customer).
  const { data: guests = [], isLoading } = useListFavouritePassengersQuery({ accountId });

  const sort = sorting[0];

  const sortedGuests = useMemo(
    () => sortGuestPassengers(guests, sort?.id as GuestPassengersSortKey | undefined, !!sort?.desc),
    [guests, sort?.id, sort?.desc]
  );

  // Reset to the first page whenever the sort changes.
  useEffect(() => setPage(1), [sort?.id, sort?.desc]);

  const totalItems = sortedGuests.length;
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, pageCount);

  const pagedGuests = useMemo(
    () => sortedGuests.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedGuests, currentPage, pageSize]
  );

  const tableData = useMemo(() => generateGuestPassengersData(pagedGuests, accountId), [pagedGuests, accountId]);

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col gap-3 pb-20 lg:pb-0">
      <div className="absolute -top-12 right-0 hidden lg:block">
        <Button variant="primary" size="sm" startIcon="Add" onClick={() => setIsAddOpen(true)}>
          Add Guest
        </Button>
      </div>

      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-neutral-mid-gray">
            <DataTable
              className="w-full overflow-x-auto !rounded-none !ring-0 [&_table]:table-auto [&_tr]:bg-white"
              data={tableData}
              columns={guestPassengersColumns}
              sorting={sorting}
              setSorting={setSorting}
              emptyState={<GuestPassengersEmptyState />}
            />
            {totalItems > 0 && (
              <div className="flex items-center border-t border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
                <Typography variant="small" className="whitespace-nowrap text-neutral-silver-gray">
                  {rangeStart}–{rangeEnd} of {totalItems}
                </Typography>
              </div>
            )}
          </div>

          {totalItems > 0 && (
            <Pagination
              className="!mt-0"
              currentPage={currentPage}
              pageCount={pageCount}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageClick={setPage}
              onSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </>
      )}

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-mid-gray bg-white px-5 py-3 lg:hidden">
        <Button variant="primary" size="md" className="!h-10 w-full" startIcon="Add" onClick={() => setIsAddOpen(true)}>
          Add Guest
        </Button>
      </div>

      <AddEditGuestModal open={isAddOpen} mode="add" accountId={accountId} onClose={() => setIsAddOpen(false)} />
    </div>
  );
};
